/**
 * Web component: Hero teaser (hero-teaser)
 * -----------------------------------------------------------------------------
 * Hero teaser with scheduled displays and copyable promo code.
 *
 */
/* eslint no-unused-vars: ['error', { 'argsIgnorePattern': 'component' }] */
import './hero-teaser.scss';

import { vueReady } from '~/core/runners';

import classes from '~/config/classes';
import timings from '~/config/timings';
import { dateTime } from '~/helpers/general';
import { checkIfActiveNonPrivate } from '~/helpers/teasers';

/**
 * DOM selectors.
 */
const selectors = {
  teaserLink: '[js-hero-teaser="link"]',
  copyButton: '[js-hero-teaser="copyButton"]',
  fallback: '[js-hero-teaser="fallback"]',
  teaser: '[js-hero-teaser="teaser"]',
  tooltip: '[js-hero-teaser="tooltip"]',
  promoText: '[js-hero-teaser="promoText"]',
};

/**
 * Create new <hero-teaser> custom element.
 */
class HeroTeaser extends HTMLElement {
  data(component) {
    return {
      selectors: {
        fallback: component.querySelector(selectors.fallback),
        teasers: [...component.querySelectorAll(selectors.teaser)],
        promoTexts: component.querySelectorAll(selectors.promoText),
        copyButtons: component.querySelectorAll(selectors.copyButton),
      },
    };
  }

  /**
   * Component lifecycle - before mount.
   */
  constructor() {
    super();
    // Apply loading class immediately before any content is rendered
    this.classList.add('is-loading');

    this.$data = this.data(this);
    this.teasersUpdated = false;

    // for initial load, read campaign state from local storage and update
    // teasers if necessary
    const { copyButtons } = this.$data.selectors;
    if (copyButtons) {
      const discountCampaigns = JSON.parse(
        localStorage.getItem('discount-campaigns'),
      );
      this.updateTeasers(discountCampaigns);

      // listen to campaign changes, update teaser content
      cnvs.EventBus.listen('discount-campaign', (campaigns) => {
        this.updateTeasers(campaigns);
      });
    }
  }

  /**
   * Component lifecycle - mounted.
   */
  connectedCallback() {
    // Loading class already added in constructor

    this.$data = Object.keys(this.$data).length ? this.$data : this.data(this);

    const { fallback, teasers } = this.$data.selectors;

    // Determine current active teaser based on time
    let selectedTeaser = this.teaserClosestToEndTime(teasers, null);

    // Now set the correct initial state before removing loading class
    if (selectedTeaser) {
      // Keep all teasers hidden by default (they already have is-hidden class)
      // Only show the active teaser
      selectedTeaser.classList.remove(classes.hidden);

      // Keep fallback hidden (it already has is-hidden class)
    } else if (fallback) {
      // No active teaser found, show fallback
      fallback.classList.remove(classes.hidden);
    }

    // Remove loading state after initial setup
    this.classList.remove('is-loading');

    // Tracking code, trigger view when selectedTeaser is in view
    if (selectedTeaser) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.handleTrackingCampaign(selectedTeaser, 'view_promotion');
            observer.disconnect();

            // Add click event listener after view event
            const selectedTeaserLink = selectedTeaser.querySelector(
              selectors.teaserLink,
            );
            if (
              selectedTeaserLink &&
              !selectedTeaserLink.classList.contains('js-hero-teaser-link')
            ) {
              selectedTeaserLink.addEventListener('click', () => {
                this.handleTrackingCampaign(selectedTeaser, 'select_promotion');
              });
              selectedTeaserLink.classList.add('js-hero-teaser-link');
            }
          }
        });
      });

      observer.observe(selectedTeaser);
    }

    this.addEventListener('click', this.handleCopy);

    // Check for changes every second
    setInterval(() => {
      const oldSelectedTeaser = selectedTeaser;
      selectedTeaser = this.teaserClosestToEndTime(teasers, selectedTeaser);

      if (selectedTeaser === null) {
        this.hideTeasers(teasers);
        this.showFallBack(teasers, fallback);
      } else if (selectedTeaser !== oldSelectedTeaser) {
        this.showTeaser(selectedTeaser, teasers, fallback);
        this.handleTrackingCampaign(selectedTeaser, 'view_promotion');

        // Add click event listener after view event
        const selectedTeaserLink = selectedTeaser.querySelector(
          selectors.teaserLink,
        );
        if (
          selectedTeaserLink &&
          !selectedTeaserLink.classList.contains('js-hero-teaser-link')
        ) {
          selectedTeaserLink.addEventListener('click', () => {
            this.handleTrackingCampaign(selectedTeaser, 'select_promotion');
          });
          selectedTeaserLink.classList.add('js-hero-teaser-link');
        }
      }
    }, 1000);
  }

  /**
   * Find teaser campaign or closest to end time from current time
   * @param {Array} teasers - Teaser elements.
   * @returns {Element} - Teaser element or null.
   */
  teaserClosestToEndTime(teasers, selectedTeaser = null) {
    let returnTeaser = null;
    const initialServerTime = parseInt(this.dataset.serverTime);

    // Calculate current server time by adding elapsed time since page load
    const pageLoadTime = performance.timeOrigin;
    const currentTime = performance.now() + pageLoadTime;
    const elapsedSeconds = Math.floor((currentTime - pageLoadTime) / 1000);
    const currentServerTime = initialServerTime + elapsedSeconds;

    const serverDate = new Date(currentServerTime * 1000);

    // check if selectedTeaser is still valid
    if (selectedTeaser !== null) {
      const result = dateTime(
        selectedTeaser.dataset.start,
        selectedTeaser.dataset.end,
        currentServerTime,
      );
      if (result) {
        returnTeaser = selectedTeaser;
      }
    }

    for (const teaser of teasers) {
      const result = dateTime(
        teaser.dataset.start,
        teaser.dataset.end,
        currentServerTime,
      );

      if (!result) {
        if (returnTeaser === teaser) {
          returnTeaser = null;
        }
        continue;
      }

      if (teaser.dataset.type === 'campaign') {
        return teaser;
      }

      if (returnTeaser === null) {
        returnTeaser = teaser;
      } else {
        try {
          // Handle 'false' end dates
          if (
            teaser.dataset.end === 'false' &&
            returnTeaser.dataset.end === 'false'
          ) {
            // If both have 'false' end dates, keep the current teaser
            return returnTeaser;
          }
          if (teaser.dataset.end === 'false') {
            // If only the new teaser has 'false' end date, use it
            returnTeaser = teaser;
          } else if (returnTeaser.dataset.end === 'false') {
            // If only the current teaser has 'false' end date, keep it
            return returnTeaser;
          } else {
            // Convert all dates to UTC milliseconds for comparison
            const teaserEndTime = new Date(`${teaser.dataset.end}Z`).getTime();
            const selectedTeaserEndTime = new Date(
              `${returnTeaser.dataset.end}Z`,
            ).getTime();
            const serverTimeMs = serverDate.getTime();

            const teaserTimeToEnd = teaserEndTime - serverTimeMs;
            const selectedTeaserTimeToEnd =
              selectedTeaserEndTime - serverTimeMs;

            if (teaserTimeToEnd <= selectedTeaserTimeToEnd) {
              returnTeaser = teaser;
            }
          }
        } catch (error) {
          console.error('Error parsing dates:', error);
          console.error('Problematic values:', {
            teaserEnd: teaser.dataset.end,
            selectedTeaserEnd: returnTeaser.dataset.end,
          });
        }
      }
    }

    // If no teaser is found, return null
    return returnTeaser;
  }

  /**
   * Shows current teaser banner.
   * @param {Element} teaser - Teaser element.
   * @param {Element} fallback - Fallback  element.
   */
  showTeaser(teaser, teasers, fallback) {
    teasers.forEach((teaserImage) => {
      teaserImage.classList.add(classes.hidden);
    });

    teaser.classList.remove(classes.hidden);

    if (fallback) {
      fallback.classList.add(classes.hidden);
    }
  }

  /**
   * Shows teasers banner.
   * @param {Array} teasers - Teaser elements.
   * @param {Element} fallback - Fallback element.
   */
  hideTeasers(teasers) {
    for (const teaser of teasers) {
      teaser.classList.add(classes.hidden);
    }
  }

  /**
   * Update teaser content based on campaign store state (code applied or not).
   * There can be multiple teasers incl. one fallback. Update all.
   * @param {Object} campaigns Can be cnvs.discount-campaigns (localStorage) or
   * object passed in 'discount-campaign' event emitted by discount-campaigns.vue
   */
  updateTeasers(campaigns) {
    this.$data = Object.keys(this.$data).length ? this.$data : this.data(this);

    const { promoTexts, copyButtons } = this.$data.selectors;

    if (!copyButtons) {
      return;
    }

    const copyButtonIcons = this.querySelectorAll(
      `${selectors.copyButton} svg`,
    );

    if (checkIfActiveNonPrivate(campaigns)) {
      const copyButtonTexts = this.querySelectorAll(
        `${selectors.copyButton} span:first-child`,
      );

      copyButtons.forEach((copyButton) => {
        copyButton.disabled = true;
      });

      copyButtonIcons.forEach((copyButtonIcon) => {
        copyButtonIcon.style.display = 'none';
      });

      copyButtonTexts.forEach((copyButtonText) => {
        copyButtonText.innerText = campaigns.active.code[0];
      });

      promoTexts.forEach((promoText) => {
        promoText.innerText = promoText.dataset.codeApplied;
      });

      this.teasersUpdated = true;
    } else if (this.teasersUpdated) {
      // reset everything to initial state
      copyButtons.forEach((copyButton) => {
        const copyButtonText = copyButton.querySelector('span');

        copyButton.disabled = false;
        copyButtonText.innerText = copyButton.dataset.code;
      });

      copyButtonIcons.forEach((copyButtonIcon) => {
        copyButtonIcon.style.display = 'unset';
      });

      promoTexts.forEach((promoText) => {
        promoText.innerText = promoText.dataset.text;
      });

      this.teasersUpdated = false;
    }
  }

  /**
   * Shows fallback if all teasers are hidden.
   * @param {Array} teasers - Teaser elements.
   * @param {Element} fallback - Fallback element.
   */
  showFallBack(teasers, fallback) {
    const allTeasersHidden = teasers.every((teaser) =>
      teaser.classList.contains('is-hidden'),
    );

    if (!allTeasersHidden) {
      return;
    }

    if (fallback) {
      fallback.classList.remove(classes.hidden);
    }
  }

  /**
   * Copy promo code and toggle tooltip.
   * @param {Event} event - Click event.
   */
  handleCopy(event) {
    if (!event.target.matches(selectors.copyButton)) {
      return;
    }

    const copyButton = event.target;
    const tooltip = event.target.querySelector(selectors.tooltip);

    if (!copyButton) {
      return;
    }

    const code = copyButton.dataset.code;
    navigator.clipboard.writeText(code);

    if (!tooltip) {
      return;
    }

    tooltip.classList.add(classes.active);

    setTimeout(() => {
      tooltip.classList.remove(classes.active);
    }, timings.message);
  }

  /**
   * Handle campaign view for dataLayer.
   * @param {HTMLElement} teaser - Teaser item.
   * @param {String} eventType - Event type:
   * (view_promotion or select_promotion).
   */
  handleTrackingCampaign(campaign, eventType = 'view_promotion') {
    cnvs.EventBus.emit(`dataLayer:${eventType}`, {
      promotion: {
        promotion_id: campaign.dataset.campaignId
          ? campaign.dataset.campaignId
          : '',
        promotion_name: campaign.dataset.campaignName
          ? campaign.dataset.campaignName
          : '',
        creative_slot: 'campaign-teaser',
        creative_name: 'Campaign teaser',
      },
    });
  }
}

/**
 * Define web component on Vue initialisation.
 */
vueReady(() => {
  if (customElements.get('hero-teaser')) {
    return;
  }

  customElements.define('hero-teaser', HeroTeaser);
});
